// Listing modes for Fuel (mod_function `fuel_tanks`) — Assets / Movements.
//
// Modes are ROUTES (PATTERNS.md §"Module weight"), not `useState`. Mirrored
// from src/groups/modes.ts (the other two-mode module whose record routes are
// siblings rather than children), itself mirrored from src/blank/BlankLayout.tsx
// (Rule 0 — the canonical reference).
//
//   <base>/assets     → FuelTanksList       (the ground fuel ASSETS register:
//                                             static tanks, refuellers,
//                                             aircraft tanks, compartments)
//   <base>/movements  → FuelMovementsList   (flat cross-tank list of
//                                             `fuel_usage_line` — receive /
//                                             deliver / transfer / uplift)
//
// WHY EXACTLY TWO, AND WHY MOVEMENTS EARNS ITS PLACE.
// Blank's rule is "never invent a second view to justify a switcher — a list →
// record navigation is a route, not a mode." Movements is not that: it is a
// different TABLE (`fuel_usage_line`, not `fuel_tanks`) with its own columns,
// and it surfaces rows that are currently unreachable from anywhere in the app.
// Measured on module_g2_demo 2026-09-04T07:03Z: **9 of 13 movement rows (2 of
// the 5 active) carry `tank_id = 0` AND `2nd_tank_id = 0`**, so no tank's
// Usage tab can show them — the per-tank query is two-sided and both sides are
// zero. Before this mode those rows had no surface at all.
//
// WHY NOT A THIRD "EXPENSES" MODE. The billing columns (`supplier`,
// `invoice_number`, `invoice_amount`, `invoice_qty`, `value`,
// `receipt_number`) live on the SAME `fuel_usage_line` rows, so an Expenses
// mode would be a filter over Movements, not a different surface — and only 3
// rows carry any money today. That is a filter, and it belongs inside
// Movements as one. Adding it as a mode would be exactly the invented second
// view Blank warns about.

import type { ModeDescriptions } from "../components/BreadcrumbDropdownMenu";

/**
 * Mode segment -> breadcrumb / dropdown label. Keys ARE the child route paths
 * in index.tsx; adding one here without a matching <Route> gives a dropdown
 * entry that 404s against the module's own route table.
 */
export const MODE_LABELS: { [segment: string]: string } = {
  assets: "Assets",
  movements: "Movements",
};

/**
 * Mode segment -> the grey line under its label in the breadcrumb dropdown
 * (BP-85, H76: "ALL model switchers should have name and description the same
 * as demand"). Same keys as the label map; each read off what the mode's
 * component renders, not paraphrased from the label.
 */
export const MODE_DESCRIPTIONS: ModeDescriptions = {
  assets: "Tanks and refuellers",
  movements: "Receipts, transfers and uplifts",
};

/**
 * Assets, and this is DATA-BACKED rather than a guess: legacy config
 * `fuel_tanks_mobile_default_mode` is `asset` on g2 (module_g2_demo config
 * table, read 2026-09-04T07:02Z) — the asset register is the surface the
 * module opens on, and "asset" is legacy's own word for it, which is why the
 * segment is `assets` and not `tanks`.
 */
export const DEFAULT_MODE_SEGMENT = "assets";

/** `<module>.viewMode` per PATTERNS.md's mode-switch storage-key shape. */
export const MODE_STORAGE_KEY = "fuel_tanks.viewMode";

/**
 * Is this a real mode segment?
 *
 * hasOwnProperty, NOT `in` — `in` walks the prototype chain, so "constructor",
 * "toString", "valueOf", "__proto__" and "hasOwnProperty" all test true against
 * an object literal. A layout validating with `in` would accept one as a mode
 * and redirect to it, 404ing the module against its own routes PERMANENTLY,
 * since the value is persisted. Regressions f74a205b / 2deb1dee; the prototype
 * chain values are enumerated in src/__tests__/routeModeSegments.test.tsx.
 */
export const isModeSegment = (segment: string): boolean =>
  Object.prototype.hasOwnProperty.call(MODE_LABELS, segment);

/**
 * Redirect target for a bare visit to the module root.
 *
 * NO LEGACY-VALUE MIGRATION: Fuel never shipped a v6 chip+menu switcher, so
 * there is no pre-existing stored value in another shape to translate. The
 * legacy `fuel_tanks_mobile_default_mode` value ('asset', singular) is a
 * SERVER config for the legacy mobile app, not a browser-stored v6 mode — it
 * informs DEFAULT_MODE_SEGMENT above but is deliberately not read here.
 */
export function readRememberedModeSegment(): string {
  if (typeof window === "undefined") return DEFAULT_MODE_SEGMENT;
  let stored: string | null = null;
  try {
    stored = window.localStorage.getItem(MODE_STORAGE_KEY);
  } catch {
    // Safari private mode / quota-blocked storage throws on access.
    return DEFAULT_MODE_SEGMENT;
  }
  return stored && isModeSegment(stored) ? stored : DEFAULT_MODE_SEGMENT;
}

/**
 * Split a live pathname into the module base and the mode segment. The base
 * is derived from the LIVE pathname rather than hardcoded, matching every
 * other converted module.
 */
export function splitModePath(pathname: string): {
  basePath: string;
  currentMode: string;
  onMode: boolean;
} {
  const segments = pathname.split("/").filter(Boolean);
  const lastSegment = segments[segments.length - 1] ?? "";
  const onMode = isModeSegment(lastSegment);
  const baseSegments = onMode ? segments.slice(0, -1) : segments;
  // Empty-check before prefixing the slash: a bare "/" + "" would make the
  // mode links "//assets", which a browser resolves as a PROTOCOL-RELATIVE URL
  // (host "assets") and navigates off-site.
  return {
    basePath: baseSegments.length ? "/" + baseSegments.join("/") : "",
    currentMode: onMode ? lastSegment : DEFAULT_MODE_SEGMENT,
    onMode,
  };
}
